import React, { useState, useEffect } from 'react';
import { SearchFilters } from '../types';
import SearchForm from '../components/SearchForm';
import JobList from '../components/JobList';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import useJobsFromRemote from '../hooks/useJobsFromRemote';
import { logUserInteraction, startTimer } from '../lib/logger';

// Custom hook to detect mobile screen size
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };

    // Check on mount
    checkIsMobile();

    // Add event listener for window resize
    window.addEventListener('resize', checkIsMobile);

    // Cleanup
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  return isMobile;
};

const JobSearchPage: React.FC = () => {
  const [filters, setFilters] = useState<SearchFilters>({});
  const [hasSearched, setHasSearched] = useState(false);
  const isMobile = useIsMobile();
  
  // Use responsive initial limit: 20 for mobile, 50 for desktop
  const initialLimit = isMobile ? 20 : 50;
  
  // Use the hook for fetching jobs
  const fetchedJobs = useJobsFromRemote({
    initialLimit, // Responsive limit for better mobile UX
    filter: {
      companyName: filters.companyName,
      jobTitle: filters.jobTitle,
      location: filters.location,
      year: filters.year,
    }
  });

  const formattedJobs = fetchedJobs.jobs.map((job) => ({
    id: job.id,
    companyName: job.employer_name,
    jobTitle: job.job_title,
    baseSalary: job.wage_rate_of_pay_from,
    location: job.worksite_city,
    year: new Date(job.decision_date).getFullYear(),
    decisionDate: job.decision_date,
  }));

  // Enhanced SEO meta tags using our custom hook
  useDocumentTitle({
    title: 'WageWise - Find H1B Visa Sponsorship Jobs | H1B Companies Database 2024',
    description: 'Discover companies that sponsor H1B visas. Search real job data, salary information, and H1B sponsorship opportunities for international professionals. Find your next career opportunity with visa sponsorship support.',
    keywords: 'H1B visa, H1B sponsorship, visa sponsorship jobs, H1B companies, work visa, employment visa, international jobs, H1B salary, visa sponsor companies, H1B job search, H1B database, immigration jobs, work permit, skilled worker visa',
    canonicalUrl: 'https://wagewise.app/',
    ogTitle: 'WageWise - Find H1B Visa Sponsorship Jobs | H1B Companies Database',
    ogDescription: 'Discover companies that sponsor H1B visas. Search real job data, salary information, and H1B sponsorship opportunities for international professionals.',
    ogImage: `${window.location.origin}/wagewise_logo_l.png`,
    ogType: 'website',
    twitterTitle: 'WageWise - Find H1B Visa Sponsorship Jobs',
    twitterDescription: 'Discover companies that sponsor H1B visas. Search real job data and salary information for international professionals.',
    twitterImage: `${window.location.origin}/wagewise_logo_l.png`
  });
  
  
  // Handler for search form submission
  const handleSearch = (searchFilters: SearchFilters) => {
    const searchTimer = startTimer('job_search_execution');
    
    setFilters(searchFilters);
    setHasSearched(true);
    
    // Log the search with detailed parameters
    logUserInteraction('search_executed', 'job_search_page', {
      filters: searchFilters,
      previouslySearched: hasSearched,
      isMobile,
      initialLimit,
    });
    
    // Reset and trigger fresh search with cursor-based pagination
    fetchedJobs.resetSearch();
    fetchedJobs.fetchData(
      searchFilters,
      null, // null cursor for initial search
      null, // null cursorId for initial search
      initialLimit, // Use responsive limit
    );
    
    // End timer when search completes (will be logged in useEffect)
    setTimeout(() => {
      searchTimer();
    }, 100);
  };
  
  // Handler for loading more jobs
  const handleLoadMore = () => {
    logUserInteraction('load_more', 'job_list', {
      currentJobCount: formattedJobs.length,
      hasMore: fetchedJobs.hasMore,
      loading: fetchedJobs.loading,
      isMobile,
    });
    fetchedJobs.loadMore();
  };
  
  return (
    <div className="max-w-6xl mx-auto py-6 md:py-12 px-4 bg-[#F8FAFC]">
      <div className="mb-6 md:mb-12 text-center">
        <h1 className="text-5xl md:text-6xl font-bold bg-clip-text mb-2 md:mb-4 tracking-tight leading-tight md:leading-relaxed py-1 md:py-2">
          Insight Meets Opportunity
        </h1>
        <p className="text-[#6B7280] max-w-2xl mx-auto font-light text-lg">
          Dive into real job data to uncover what companies really offer
        </p>
        </div>
      
      <div className="mb-6 md:mb-12">
        <SearchForm onSearch={handleSearch} />
      </div>
      
      {hasSearched && (
        <div>
          <h2 className="text-xl font-semibold mb-4">
            Results 
            <span className="text-sm font-normal text-[#6B7280] ml-2">(sorted by salary, highest first)</span>
          </h2>
          <JobList 
            jobs={formattedJobs} 
            loading={fetchedJobs.loading}
            hasMore={fetchedJobs.hasMore}
            onLoadMore={handleLoadMore}
          />
        </div>
      )}
    </div>
  );
};

export default JobSearchPage; 